import PiniaInterface from "./lib/pinia-interface";

export interface EntityWrapper<T = any> {
	data: T;
	expiration_date?: Date | string | number | null;
	is_permanent?: boolean;
}

export default class StoreInterfaceClass {
	private store: any;

	constructor() {
		this._set_store_library(PiniaInterface);
	}

	private _set_store_library(store: any): void {
		this.store = store;
	}

	/**
	 * Sets an entity dataset inside the reactive store with optional expiration metadata.
	 */
	public set<T = any>(
		entity_name: string,
		data: T,
		expiration_date: Date | string | number | null = null,
		is_permanent: boolean | null = null
	): void {
		const entity_data: EntityWrapper<T> = {
			data: data
		};

		if (expiration_date) {
			entity_data.expiration_date = expiration_date;
		}
		if (is_permanent) {
			entity_data.is_permanent = true;
		}

		this.store.set_entity(entity_name, entity_data);
	}

	/**
	 * Retrieves an entity or nested key property if unexpired.
	 */
	public get<T = any>(
		entity: string,
		key: string | null = null
	): T | undefined {
		const result: EntityWrapper<T> | undefined =
			this.store.get_full_entity(entity);

		if (
			result === undefined ||
			(result.expiration_date && new Date(result.expiration_date) < new Date())
		) {
			return undefined;
		} else if (result.data && key) {
			const nested_data = (result.data as Record<string, any>)[key];
			if (nested_data === undefined) return undefined;

			if (nested_data && nested_data.expiration_date) {
				if (new Date(nested_data.expiration_date) > new Date()) {
					return nested_data.data as T;
				} else {
					return undefined;
				}
			} else {
				return nested_data as T;
			}
		} else {
			return result.data;
		}
	}

	/**
	 * Searches an array entity for the first entry matching a target property value.
	 */
	public find_by_property<T = any>(
		entity_name: string,
		property_name: string,
		property_value: any
	): T | null {
		const entity_values = this.get<T[]>(entity_name);

		return entity_values
			? entity_values.find(
					(value: any) => value[property_name] === property_value
				) || null
			: null;
	}

	/**
	 * Updates an existing entry inside an array or appends a new one if not present.
	 */
	public update_or_create_by_property<T = any>(
		entity_name: string,
		property_name: string,
		property_value: any,
		new_data: T
	): void {
		const entity_data = this.get<any>(entity_name);

		if (!entity_data) {
			return this.set(entity_name, [new_data]);
		}

		if (Array.isArray(entity_data)) {
			let found_in_array = false;

			for (let index = 0; index < entity_data.length; index++) {
				if (entity_data[index][property_name] === property_value) {
					entity_data[index] = new_data;
					found_in_array = true;
					break;
				}
			}

			if (!found_in_array) {
				entity_data.push(new_data);
			}
		}

		const entity_expiration_date =
			this._get_entity_expiration_date(entity_name);
		this.set(entity_name, entity_data, entity_expiration_date);
	}

	/**
	 * Removes an entity item by index position.
	 */
	public delete(entity_name: string, index: number | string): void {
		this.store.remove_entity_index(entity_name, index);
	}

	/**
	 * Deletes all items in an array/dictionary matching a specific property value.
	 */
	public delete_by_property(
		entity_name: string,
		property_name: string,
		property_value: any
	): void {
		const entity_values = this.get<any>(entity_name);
		if (!entity_values) return;

		const index_to_delete: (string | number)[] = [];

		for (const index in entity_values) {
			if (entity_values[index][property_name] === property_value) {
				index_to_delete.push(index);
			}
		}

		index_to_delete.forEach((index) => this.delete(entity_name, index));
	}

	/**
	 * Completely clears data values associated with an entity name.
	 */
	public delete_entity_values(entity_name: string): void {
		this.store.remove_entity_values(entity_name);
	}

	/**
	 * Deletes all entities with option to preserve permanent flag entries.
	 */
	public delete_all_entities(
		include_permanent_entities: boolean | null = null
	): void {
		if (include_permanent_entities) {
			this.store.remove_all_entities();
		} else {
			const all_entities_data = this.store.get_all_entities_data();

			for (const entity_name in all_entities_data) {
				const entity_data = all_entities_data[entity_name];

				if (!entity_data?.is_permanent) {
					this.store.remove_entity(entity_name);
				}
			}
		}
	}

	/**
	 * Helper retrieving current expiration date for a target entity.
	 */
	private _get_entity_expiration_date(
		entity_name: string
	): Date | string | number | null {
		const full_entity_data = this.store.get_full_entity(entity_name);
		return full_entity_data?.expiration_date || null;
	}

	/**
	 * Locates an element inside a cached array using loose equality check.
	 */
	public find_in_array<T = any>(
		entity_name: string,
		find_by: string,
		value: any
	): T | undefined {
		const entity_values = this.get<T[]>(entity_name);
		return entity_values
			? entity_values.find((element: any) => element[find_by] == value)
			: undefined;
	}

	/**
	 * Pushes an element to a cached array or initializes a new array entity if absent.
	 */
	public push_to_array<T = any>(entity_name: string, element: T): void {
		const array_values = this.get<any>(entity_name);
		const cached_data_is_array = Array.isArray(array_values);

		if (cached_data_is_array) {
			this.store.push_to_array(entity_name, element);
		} else {
			this.set(entity_name, [element]);
		}
	}

	/**
	 * Locates and removes a target matching element inside an array entity.
	 */
	public find_and_delete_in_array(
		entity_name: string,
		find_by: string,
		value: any
	): void {
		const entity_values = this.get<any[]>(entity_name);
		if (!entity_values) return;

		let key: number | null = null;
		for (let i = 0; i < entity_values.length; i++) {
			if (entity_values[i][find_by] == value) {
				key = i;
				break;
			}
		}

		if (key !== null) {
			if (typeof this.store.remove_entity_index === "function") {
				this.store.remove_entity_index(entity_name, key);
			} else if (typeof this.store.commit === "function") {
				this.store.commit("remove_entity_index", {
					entity: entity_name,
					index: key
				});
			}
		}
	}
}
